import React, { useState, useEffect } from 'react';
import { createSiteDocument, getAdminDocuments, updateSiteDocument, deleteSiteDocument, getDocumentInteractions } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  FileText, 
  Plus, 
  Trash2,
  Edit,
  Eye,
  Users,
  Shield,
  FileQuestion,
  FileCheck
} from 'lucide-react';
import { toast } from 'sonner';

const DOC_TYPES = [
  { id: 'service_agreement', label: 'Service Agreement', icon: FileCheck, color: 'text-blue-400' },
  { id: 'nda', label: 'NDA', icon: Shield, color: 'text-purple-400' },
  { id: 'file_usage', label: 'File Usage', icon: FileText, color: 'text-green-400' },
  { id: 'questionnaire', label: 'Questionnaire', icon: FileQuestion, color: 'text-orange-400' },
];

export default function DocumentsPage() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [interactionsDialogOpen, setInteractionsDialogOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [interactions, setInteractions] = useState([]);
  const [newDoc, setNewDoc] = useState({
    title: '',
    doc_type: 'service_agreement',
    content: '',
    requires_signature: true,
    is_active: true
  });

  useEffect(() => {
    fetchDocuments();
  }, [activeTab]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const docType = activeTab === 'all' ? undefined : activeTab;
      const response = await getAdminDocuments(docType);
      setDocuments(response.data);
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await createSiteDocument(newDoc);
      toast.success('Document created successfully');
      setCreateDialogOpen(false);
      setNewDoc({
        title: '',
        doc_type: 'service_agreement',
        content: '',
        requires_signature: true,
        is_active: true
      });
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create document');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedDoc) return;
    try {
      await updateSiteDocument(selectedDoc.id, {
        title: selectedDoc.title,
        content: selectedDoc.content,
        requires_signature: selectedDoc.requires_signature,
        is_active: selectedDoc.is_active
      });
      toast.success('Document updated');
      setEditDialogOpen(false);
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to update document');
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Delete this document? This will also remove all client interactions.')) return;
    try {
      await deleteSiteDocument(docId);
      toast.success('Document deleted');
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleViewInteractions = async (doc) => {
    setSelectedDoc(doc);
    try {
      const response = await getDocumentInteractions(doc.id);
      setInteractions(response.data);
      setInteractionsDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load interactions');
    }
  };

  const getDocTypeInfo = (type) => DOC_TYPES.find(d => d.id === type) || DOC_TYPES[0];

  return (
    <div className="space-y-6" data-testid="admin-documents-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Site Documents</h1>
          <p className="text-slate-400">Manage agreements, NDAs, and questionnaires for clients</p>
        </div>
        
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#4A90D9] hover:bg-[#3A80C9]" data-testid="create-document-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Document
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#111827] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">Create New Document</DialogTitle>
              <DialogDescription className="text-slate-400">
                Create a new site-wide document for clients to view and interact with.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <Label className="text-slate-300">Document Type *</Label>
                <Select 
                  value={newDoc.doc_type} 
                  onValueChange={(value) => setNewDoc({ ...newDoc, doc_type: value })}
                >
                  <SelectTrigger className="mt-1 bg-[#1E293B] border-slate-700 text-white" data-testid="doc-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1E293B] border-slate-700">
                    {DOC_TYPES.map((type) => (
                      <SelectItem key={type.id} value={type.id}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Title *</Label>
                <Input
                  value={newDoc.title}
                  onChange={(e) => setNewDoc({ ...newDoc, title: e.target.value })}
                  required
                  placeholder="e.g., Service Agreement 2024"
                  className="mt-1 bg-[#1E293B] border-slate-700 text-white"
                  data-testid="doc-title-input"
                />
              </div>
              <div>
                <Label className="text-slate-300">Content *</Label>
                <Textarea
                  value={newDoc.content}
                  onChange={(e) => setNewDoc({ ...newDoc, content: e.target.value })}
                  required
                  placeholder={newDoc.doc_type === 'questionnaire' 
                    ? "Enter questions, one per line:\n1. What services are you interested in?\n2. What is your budget range?\n3. What is your timeline?"
                    : "Enter the document content..."}
                  className="mt-1 bg-[#1E293B] border-slate-700 text-white min-h-[200px]"
                  data-testid="doc-content-input"
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={newDoc.requires_signature}
                    onCheckedChange={(checked) => setNewDoc({ ...newDoc, requires_signature: checked })}
                    data-testid="requires-signature-switch"
                  />
                  <Label className="text-slate-300">Requires Signature/Acknowledgment</Label>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={newDoc.is_active}
                    onCheckedChange={(checked) => setNewDoc({ ...newDoc, is_active: checked })}
                    data-testid="is-active-switch"
                  />
                  <Label className="text-slate-300">Active (visible to clients)</Label>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="ghost" onClick={() => setCreateDialogOpen(false)} className="text-slate-400">
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#4A90D9] hover:bg-[#3A80C9]" data-testid="submit-document-btn">
                  Create Document
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[#1E293B] border border-white/10">
          <TabsTrigger value="all" className="data-[state=active]:bg-[#4A90D9]">All</TabsTrigger>
          {DOC_TYPES.map((type) => (
            <TabsTrigger key={type.id} value={type.id} className="data-[state=active]:bg-[#4A90D9]">
              {type.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Documents List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      ) : documents.length === 0 ? (
        <Card className="bg-[#111827] border-white/10">
          <CardContent className="py-16">
            <div className="text-center">
              <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400 mb-4">No documents found</p>
              <Button onClick={() => setCreateDialogOpen(true)} className="bg-[#4A90D9] hover:bg-[#3A80C9]">
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Document
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {documents.map((doc) => {
            const typeInfo = getDocTypeInfo(doc.doc_type);
            const TypeIcon = typeInfo.icon;
            return (
              <Card key={doc.id} className="bg-[#111827] border-white/10" data-testid={`document-${doc.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-lg bg-white/5 flex items-center justify-center ${typeInfo.color}`}>
                        <TypeIcon className="w-6 h-6" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-lg font-bold text-white">{doc.title}</h3>
                          {!doc.is_active && <Badge className="badge-draft">Inactive</Badge>}
                          {doc.requires_signature && <Badge className="badge-awaiting_approval">Requires Signature</Badge>}
                        </div>
                        <p className="text-sm text-slate-400 mb-2">{typeInfo.label}</p>
                        <p className="text-sm text-slate-500 line-clamp-2">{doc.content.substring(0, 150)}...</p>
                        <p className="text-xs text-slate-500 mt-2">
                          Created {new Date(doc.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleViewInteractions(doc)}
                        className="text-slate-400 hover:text-white"
                        data-testid={`view-interactions-${doc.id}`}
                      >
                        <Users className="w-4 h-4 mr-1" />
                        Responses
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => { setSelectedDoc(doc); setViewDialogOpen(true); }}
                        className="text-slate-400 hover:text-white"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => { setSelectedDoc({...doc}); setEditDialogOpen(true); }}
                        className="text-[#4A90D9] hover:text-[#38BDF8]"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDelete(doc.id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* View Document Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="bg-[#111827] border-white/10 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">{selectedDoc?.title}</DialogTitle>
          </DialogHeader>
          <div className="contract-content text-sm text-slate-300 bg-[#0B1120] p-4 rounded whitespace-pre-wrap">
            {selectedDoc?.content}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Document Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-[#111827] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Document</DialogTitle>
          </DialogHeader>
          {selectedDoc && (
            <form onSubmit={handleUpdate} className="space-y-4">
              <div>
                <Label className="text-slate-300">Title</Label>
                <Input
                  value={selectedDoc.title}
                  onChange={(e) => setSelectedDoc({ ...selectedDoc, title: e.target.value })}
                  className="mt-1 bg-[#1E293B] border-slate-700 text-white"
                />
              </div>
              <div>
                <Label className="text-slate-300">Content</Label>
                <Textarea
                  value={selectedDoc.content}
                  onChange={(e) => setSelectedDoc({ ...selectedDoc, content: e.target.value })}
                  className="mt-1 bg-[#1E293B] border-slate-700 text-white min-h-[200px]"
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={selectedDoc.requires_signature}
                  onCheckedChange={(checked) => setSelectedDoc({ ...selectedDoc, requires_signature: checked })}
                />
                <Label className="text-slate-300">Requires Signature</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={selectedDoc.is_active}
                  onCheckedChange={(checked) => setSelectedDoc({ ...selectedDoc, is_active: checked })}
                />
                <Label className="text-slate-300">Active</Label>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="ghost" onClick={() => setEditDialogOpen(false)} className="text-slate-400">
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#4A90D9] hover:bg-[#3A80C9]">
                  Save Changes
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Interactions Dialog */}
      <Dialog open={interactionsDialogOpen} onOpenChange={setInteractionsDialogOpen}>
        <DialogContent className="bg-[#111827] border-white/10 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Client Responses: {selectedDoc?.title}</DialogTitle>
            <DialogDescription className="text-slate-400">
              View all client interactions with this document
            </DialogDescription>
          </DialogHeader>
          {interactions.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">No client interactions yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {interactions.map((interaction) => (
                <div key={interaction.id} className="p-4 rounded-lg bg-white/5 border border-white/10">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-white font-medium">{interaction.user_name}</p>
                    <Badge className={`badge-${interaction.status === 'signed' ? 'approved' : 'in_progress'}`}>
                      {interaction.status}
                    </Badge>
                  </div>
                  {interaction.signed_at && (
                    <p className="text-sm text-slate-400">
                      Signed: {new Date(interaction.signed_at).toLocaleString()}
                    </p>
                  )}
                  {interaction.responses && (
                    <div className="mt-3 p-3 bg-[#0B1120] rounded">
                      <p className="text-xs text-slate-500 mb-2">Questionnaire Responses:</p>
                      <pre className="text-sm text-slate-300 whitespace-pre-wrap">
                        {JSON.stringify(interaction.responses, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
